import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "title" }
const _hoisted_2 = { class: "methods" }
const _hoisted_3 = {
  key: 0,
  class: "method-empty"
}
const _hoisted_4 = {
  key: 1,
  class: "method"
}
const _hoisted_5 = { class: "remove" }
const _hoisted_6 = { class: "code" }
const _hoisted_7 = { class: "name" }
const _hoisted_8 = { class: "title" }

import { computed, onMounted, ref } from "vue";
import { orderBy, debounce } from "lodash-es";
import { useI18n } from "vue-i18n";
import { useRouter, useRoute } from "vue-router";
import store from "@/store";

import { toast } from "@/common/utils/toast";

import { formGet, formPatch, formPut } from "@/common/api/rest/form/form";
import {
  questionAdd,
  questionEdit,
  questionDel,
} from "@/common/api/rest/question";

import type { MethodItemType } from "@/common/types/method";
import type { FormType, FormPayloadType } from "@/common/types/form";
import type {
  QuestionType,
  QuestionPayloadType,
} from "@/common/types/question";

import { RegisterTheme } from "@/common/themes";
import { formsForm, questionForm, searchMethodItem } from "@/components";
import {
  svgIcon,
  baseTable,
  baseModal,
  baseConfirm,
  baseTooltip,
} from "@/common/components";


export default /*@__PURE__*/_defineComponent({
  __name: '[report]',
  setup(__props) {

const { t } = useI18n();
const router = useRouter();
const route = useRoute();

const tableHeader = computed(() => [
  { key: "ordering", label: t("common.labels.order") },
  { key: "name", label: t("common.labels.name") },
  { key: "type", label: t("common.labels.type") },
  { key: "required", label: t("common.labels.required") },
]);

const tableData = ref<QuestionType[]>([]);

const methodItem = ref<MethodItemType>();

const formPayload = ref<FormPayloadType | null>(null);
const questionPayload = ref<QuestionPayloadType | null>(null);
const selected = ref<QuestionType | null>(null);
const seletedMethod = ref<MethodItemType | null>(null);

const isLoading = ref(false);
const isEdit = ref(false);
const showQuestionAdd = ref(false);
const showQuestionDel = ref(false);
const showMethodAdd = ref(false);
const showMethodDel = ref(false);

const accountId = computed(() => String(route.params.id));
const formId = computed(() => String(route.params.form));

const delMessage = computed(() => {
  const message = t("question.texts.del_confirm", {
    concat: selected.value?.name,
  });
  const undo = t("common.texts.undo_message");

  return `${message}<br>${undo}`;
});

function back() {
  router.push(`/${accountId.value}/register/form`);
}

function fetchForm() {
  isLoading.value = true;

  formGet(formId.value)
    .then((response) => {
      const { success, data } = response;

      if (success) {
        console.log("data", data);
        formPayload.value = {
          name: data.name,
          code: data.code,
          description: data.description,
        };
        methodItem.value = data.method_item;
        tableData.value = orderBy(data.questions, "ordering");
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      isLoading.value = false;
    });
}

function patchData(data: Partial<FormType>) {
  formPatch(formId.value, data)
    .then((response) => {
      const { success, data } = response;

      if (success) {
        fetchForm();
        toast({ message: data.message, type: "success" });
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function updateForm(data: FormPayloadType) {
  formPut(formId.value, data)
    .then((response) => {
      const { success, data } = response;

      if (success) {
        fetchForm();
        toast({ message: data.message, type: "success" });
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function addQuestion() {
  showQuestionAdd.value = true;
}

function addQuestionCancel() {
  isEdit.value = false;
  selected.value = null;
  questionPayload.value = null;
  showQuestionAdd.value = false;
}

function addQuestionConfirm() {
  if (!questionPayload.value) return;

  store.commit("SET_LOADING", true);

  questionAdd(formId.value as string, questionPayload.value)
    .then((response) => {
      const { success, data } = response;

      if (success) {
        fetchForm();
        addQuestionCancel();
        toast({ message: data.message, type: "success" });
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function editQuestion(data: QuestionType) {
  questionPayload.value = data;
  selected.value = data;
  isEdit.value = true;
  showQuestionAdd.value = true;
}

function editQuestionConfirm() {
  if (!questionPayload.value) return;

  store.commit("SET_LOADING", true);

  questionEdit(selected.value?.question_id as string, questionPayload.value)
    .then((response) => {
      const { success, data } = response;

      if (success) {
        fetchForm();
        addQuestionCancel();
        toast({ message: data.message, type: "success" });
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function delQuestion(data: QuestionType) {
  selected.value = data;
  showQuestionDel.value = true;
}

function delQuestionCancel() {
  selected.value = null;
  showQuestionDel.value = false;
}

function delQuestionConfirm() {
  store.commit("SET_LOADING", true);

  questionDel(selected.value?.question_id as string)
    .then((response) => {
      const { success, data } = response;

      if (success) {
        fetchForm();
        delQuestionCancel();
        toast({ message: data.message, type: "success" });
      } else {
        toast({ message: data.message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    })
    .finally(() => {
      store.commit("SET_LOADING", false);
    });
}

function addMethod() {
  showMethodAdd.value = true;
}

function addMethodCancel() {
  showMethodAdd.value = false;
  seletedMethod.value = null;
}

function addMethodSelect(data: MethodItemType) {
  seletedMethod.value = data;
}

function addMethodConfirm() {
  patchData({
    method_id: seletedMethod.value?.method_id,
    method_item_id: seletedMethod.value?.item_id,
  });
  addMethodCancel();
}

function delMethod() {
  showMethodDel.value = true;
}

function delMethodCancel() {
  showMethodDel.value = false;
}

function delMethodConfirm() {
  patchData({
    method_id: null,
    method_item_id: null,
  });

  showMethodDel.value = false;
}

function questionSubmit() {
  if (isEdit.value) editQuestionConfirm();
  else addQuestionConfirm();
}

const debounceForm = debounce(updateForm, 1000);

onMounted(() => {
  if (formId.value) {
    fetchForm();
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(RegisterTheme), { "tab-active": "form" }, {
      title: _withCtx(() => [
        _createElementVNode("div", {
          class: "btn btn-back btn-sm",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (back()))
        }, [
          _createVNode(_unref(svgIcon), {
            icon: "arrows-left",
            size: "1.25rem"
          })
        ]),
        _createTextVNode(" " + _toDisplayString(_unref(t)("form.texts.edit_form")), 1)
      ]),
      content: _withCtx(() => [
        _createVNode(_unref(formsForm), {
          width: "100%",
          data: formPayload.value,
          onUpdateData: _unref(debounceForm)
        }, null, 8, ["data", "onUpdateData"]),
        _createElementVNode("div", _hoisted_1, _toDisplayString(_unref(t)("common.labels.method")), 1),
        _createElementVNode("div", _hoisted_2, [
          (!methodItem.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createTextVNode(_toDisplayString(_unref(t)("form.texts.empty_method")) + " ", 1),
                _createElementVNode("button", {
                  class: "btn btn-md btn-primary",
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (addMethod()))
                }, _toDisplayString(_unref(t)("common.labels.add").toUpperCase()), 1)
              ]))
            : (_openBlock(), _createElementBlock("div", _hoisted_4, [
                _createElementVNode("div", _hoisted_5, [
                  _createVNode(_unref(baseTooltip), { position: "relative" }, {
                    trigger: _withCtx(() => [
                      _createElementVNode("button", {
                        type: "button",
                        class: "btn btn-none btn-sm remove",
                        onClick: _cache[2] || (_cache[2] = ($event: any) => (delMethod()))
                      }, [
                        _createVNode(_unref(svgIcon), {
                          icon: "trash",
                          size: "1.75rem"
                        })
                      ])
                    ]),
                    tooltip: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref(t)("common.labels.delete")), 1)
                    ]),
                    _: 1
                  })
                ]),
                _createElementVNode("div", _hoisted_6, _toDisplayString(methodItem.value.code), 1),
                _createElementVNode("div", _hoisted_7, _toDisplayString(methodItem.value.name), 1)
              ]))
        ]),
        _createElementVNode("div", _hoisted_8, _toDisplayString(_unref(t)("common.labels.questions")), 1),
        _createVNode(_unref(baseTable), {
          header: tableHeader.value,
          data: tableData.value,
          loading: isLoading.value,
          options: true,
          edit: true,
          del: true,
          paginated: false,
          "onBtn:add": _cache[3] || (_cache[3] = ($event: any) => (addQuestion())),
          "onBtn:edit": editQuestion,
          "onBtn:del": delQuestion
        }, {
          type: _withCtx(({ data }) => [
            _createTextVNode(_toDisplayString(_unref(t)(`question.types.${data.type}`)), 1)
          ]),
          required: _withCtx(({ data }) => [
            (data.required)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createTextVNode(_toDisplayString(_unref(t)("common.labels.yes")), 1)
                ], 64))
              : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  _createTextVNode(_toDisplayString(_unref(t)("common.labels.not")), 1)
                ], 64))
          ]),
          _: 1
        }, 8, ["header", "data", "loading"])
      ]),
      _: 1
    }),
    _createVNode(_unref(baseModal), {
      width: "80%",
      show: showQuestionAdd.value,
      "show-button-confirm": true,
      "button-confirm": _unref(t)('common.labels.confirm'),
      "button-close": _unref(t)('common.labels.cancel'),
      onClose: _cache[5] || (_cache[5] = ($event: any) => (addQuestionCancel())),
      onConfirm: _cache[6] || (_cache[6] = ($event: any) => (questionSubmit()))
    }, {
      content: _withCtx(() => [
        _createVNode(_unref(questionForm), {
          width: "100%",
          order: tableData.value.length + 1,
          title: 
          isEdit.value
            ? _unref(t)('question.texts.edit_question')
            : _unref(t)('question.texts.add_question')
        ,
          data: questionPayload.value,
          onUpdateData: _cache[4] || (_cache[4] = ($event: any) => (questionPayload.value = $event))
        }, null, 8, ["order", "title", "data"])
      ]),
      _: 1
    }, 8, ["show", "button-confirm", "button-close"]),
    _createVNode(_unref(baseModal), {
      show: showMethodAdd.value,
      title: 
      _unref(t)('common.labels.add', {
        concat: _unref(t)('common.labels.method'),
      }).toUpperCase()
    ,
      "show-button-confirm": true,
      "button-confirm": _unref(t)('common.labels.add'),
      "button-close": _unref(t)('common.labels.cancel'),
      onClose: _cache[7] || (_cache[7] = ($event: any) => (addMethodCancel())),
      onConfirm: _cache[8] || (_cache[8] = ($event: any) => (addMethodConfirm()))
    }, {
      content: _withCtx(() => [
        _createVNode(_unref(searchMethodItem), {
          width: "600px",
          "account-id": accountId.value,
          onUpdateData: addMethodSelect
        }, null, 8, ["account-id"])
      ]),
      _: 1
    }, 8, ["show", "title", "button-confirm", "button-close"]),
    _createVNode(_unref(baseConfirm), {
      show: showQuestionDel.value,
      title: _unref(t)('question.texts.del_question').toUpperCase(),
      message: delMessage.value,
      "max-width": "500px",
      onCancel: _cache[9] || (_cache[9] = ($event: any) => (delQuestionCancel())),
      onConfirm: _cache[10] || (_cache[10] = ($event: any) => (delQuestionConfirm()))
    }, null, 8, ["show", "title", "message"]),
    _createVNode(_unref(baseConfirm), {
      show: showMethodDel.value,
      title: 
      _unref(t)('common.labels.delete', {
        concat: _unref(t)('common.labels.method'),
      }).toUpperCase()
    ,
      message: 
      _unref(t)('form.texts.del_method', {
        concat: methodItem.value?.code,
      })
    ,
      "max-width": "500px",
      onCancel: _cache[11] || (_cache[11] = ($event: any) => (delMethodCancel())),
      onConfirm: _cache[12] || (_cache[12] = ($event: any) => (delMethodConfirm()))
    }, null, 8, ["show", "title", "message"])
  ], 64))
}
}

})